import * as React from "react";
import { Box, TableContainer, TableContainerProps, useTheme, alpha, useMediaQuery } from "@mui/material";

export interface GlassTableProps extends Omit<TableContainerProps, "sx"> {
  children: React.ReactNode;
  maxHeight?: number | { xs?: number; md?: number; };
  sx?: TableContainerProps["sx"];
  renderMobileList?: () => React.ReactNode;
  mobileListData?: Array<Record<string, any>>; // Optional: provide data for auto list generation
  hasData?: boolean; // Optional: set to false to hide table header when no data
}

const GlassTable = React.forwardRef<HTMLDivElement, GlassTableProps>(
  ({ children, maxHeight = { xs: 420, md: 560 }, sx, renderMobileList, mobileListData, hasData, ...props }, ref) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const maxHeightValue = typeof maxHeight === "number"
      ? maxHeight
      : maxHeight;

    // Conditional rendering: show mobile list if renderMobileList is provided
    // if (isMobile && renderMobileList) {
    //   return (
    //     <Box
    //       ref={ref}
    //       sx={{
    //         position: "relative",
    //         width: "100%",
    //         maxHeight: maxHeightValue,
    //         borderRadius: "18px",
    //         overflow: "hidden",
    //         isolation: "isolate",
    //         background: isDark
    //           ? `linear-gradient(135deg, ${alpha("#0d1b2a", 0.92)} 0%, ${alpha("#0d1b2a", 0.85)} 45%, ${alpha("#F4A259", 0.65)} 100%)`
    //           : `linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.82) 50%, rgba(244, 162, 89, 0.35) 100%)`,
    //         backdropFilter: "blur(24px) saturate(140%)",
    //         WebkitBackdropFilter: "blur(24px) saturate(140%)",
    //         border: `1.5px solid ${isDark ? alpha("#ffffff", 0.4) : alpha("#ffffff", 0.8)}`,
    //         boxShadow: isDark
    //           ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
    //           : "0 8px 32px rgba(244, 162, 89, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
    //         "&::before": {
    //           content: '""',
    //           position: "absolute",
    //           top: 0,
    //           left: 0,
    //           right: 0,
    //           bottom: 0,
    //           borderRadius: "18px",
    //           pointerEvents: "none",
    //           boxShadow: isDark
    //             ? "inset 0 1px 0 0 rgba(255, 255, 255, 0.3)"
    //             : "inset 0 1px 0 0 rgba(255, 255, 255, 0.6)",
    //           zIndex: 1,
    //         },
    //         ...sx,
    //       }}
    //     >
    //       {/* Gradient overlays for glass shine effect */}
    //       <Box
    //         sx={{
    //           position: "absolute",
    //           top: 0,
    //           left: 0,
    //           right: 0,
    //           height: "50%",
    //           background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.15 : 0.25)} 0%, transparent 100%)`,
    //           pointerEvents: "none",
    //           zIndex: 1,
    //           borderRadius: "18px 18px 0 0",
    //         }}
    //       />
    //       <Box
    //         sx={{
    //           position: "absolute",
    //           top: 0,
    //           left: 0,
    //           right: 0,
    //           bottom: 0,
    //           background: `linear-gradient(135deg, transparent 0%, ${alpha("#F4A259", 0.03)} 100%)`,
    //           pointerEvents: "none",
    //           zIndex: 1,
    //           borderRadius: "18px",
    //         }}
    //       />
    //       <Box
    //         sx={{
    //           position: "relative",
    //           zIndex: 2,
    //           height: "100%",
    //           maxHeight: maxHeightValue,
    //           overflow: "auto",
    //           p: 2,
    //         }}
    //       >
    //         {renderMobileList()}
    //       </Box>
    //     </Box>
    //   );
    // }

    return (
      <Box
        ref={ref}
        sx={{
          position: "relative",
          width: "100%",
          maxHeight: maxHeightValue,
          borderRadius: theme.shape.borderRadius * 1.5,
          overflow: "hidden",
          isolation: "isolate",
          background: isDark
            ? `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.88)} 100%)`
            : `linear-gradient(135deg, ${alpha("#ffffff", 0.95)} 0%, ${alpha("#ffffff", 0.88)} 100%)`,
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          border: `1px solid ${isDark ? alpha("#ffffff", 0.12) : alpha("#000000", 0.08)}`,
          boxShadow: isDark
            ? "0 4px 20px rgba(0, 0, 0, 0.25), 0 1px 4px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
            : "0 4px 20px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: theme.shape.borderRadius * 1.5,
            pointerEvents: "none",
            boxShadow: isDark
              ? "inset 0 1px 0 0 rgba(255, 255, 255, 0.08)"
              : "inset 0 1px 0 0 rgba(255, 255, 255, 0.9)",
            zIndex: 1,
          },
          "&:hover": {
            boxShadow: isDark
              ? "0 6px 24px rgba(0, 0, 0, 0.3), 0 2px 6px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.12)"
              : "0 6px 24px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.85)",
          },
        }}
      >
        {/* Subtle gradient overlay for glass shine effect */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "40%",
            background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.08 : 0.2)} 0%, transparent 100%)`,
            pointerEvents: "none",
            zIndex: 1,
            borderRadius: `${theme.shape.borderRadius * 1.5}px ${theme.shape.borderRadius * 1.5}px 0 0`,
          }}
        />
        <TableContainer
          {...props}
          sx={{
            width: "100%",
            maxHeight: maxHeightValue,
            borderRadius: theme.shape.borderRadius * 1.5,
            overflow: "auto",
            position: "relative",
            zIndex: 2,
            backgroundColor: "transparent",
            // Custom scrollbar styling
            "&::-webkit-scrollbar": {
              width: 8,
              height: 8,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isDark ? alpha("#ffffff", 0.2) : alpha("#000000", 0.2),
              borderRadius: 4,
              "&:hover": {
                backgroundColor: isDark ? alpha("#ffffff", 0.3) : alpha("#000000", 0.3),
              },
            },
            // Hide table header (thead) when hasData is false
            "& table thead": {
              display: hasData === false ? "none" : "table-header-group",
            },
            // Table styling improvements
            "& table": {
              backgroundColor: "transparent",
              borderCollapse: "separate",
              borderSpacing: 0,
            },
            // Remove all default borders
            "& table td, & table th": {
              border: "none",
              borderBottom: `1px solid ${isDark ? alpha("#ffffff", 0.06) : alpha("#000000", 0.04)}`,
            },
            // Header cell styling
            "& table thead th": {
              borderBottom: `1px solid ${isDark ? alpha("#ffffff", 0.1) : alpha("#000000", 0.08)}`,
              backgroundColor: isDark ? alpha("#ffffff", 0.03) : alpha("#000000", 0.02),
              fontWeight: 600,
              textTransform: "uppercase",
              fontSize: "0.75rem",
              letterSpacing: "0.5px",
              color: isDark ? alpha("#ffffff", 0.7) : alpha("#000000", 0.6),
              padding: theme.spacing(1.5, 2),
            },
            // Body cell styling
            "& table tbody td": {
              padding: theme.spacing(1.5, 2),
              borderBottom: `1px solid ${isDark ? alpha("#ffffff", 0.04) : alpha("#000000", 0.03)}`,
            },
            // Last row - no border
            "& table tbody tr:last-child td": {
              borderBottom: "none",
            },
            // Row hover effect
            "& table tbody tr": {
              transition: "background-color 0.2s ease",
              "&:hover": {
                backgroundColor: isDark ? alpha("#ffffff", 0.04) : alpha("#000000", 0.02),
              },
            },
            ...sx,
          }}
        >
          {children}
        </TableContainer>
      </Box>
    );
  }
);

GlassTable.displayName = "GlassTable";

export default GlassTable;

